import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useMst } from '../../../models/stores/Root';
import UtilityService from '../../../services/UtilityService';
import { ItemContract, ItemOptionContract } from '../../../types/Item';
import ErrorLabel from '../../input/ErrorLabel';
import CloseIcon from '../CloseIcon';
import ErrorModal from '../ErrorModal';

export const CartItemTestId = 'cartitem-content';

export type CartItemProps = Pick<ItemContract, 'imgUrl' | 'qty'> & {
  id?: number;
  name?: string;
  options?: ItemOptionContract[];
  setupCost: number;
  unitPrice: number;
  currency: string;
  cartSubTotal: number;
  handleRemoveItem: (
    id: number,
    options: ItemOptionContract[]
  ) => Promise<void>;
};

export const CartItem: React.FC<CartItemProps> = observer(
  ({
    name = '',
    imgUrl,
    options = [],
    qty,
    setupCost,
    unitPrice,
    currency,
    cartSubTotal,
    id,
    handleRemoveItem,
  }: CartItemProps) => {
    const rootStore = useMst();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { cart } = rootStore;
    const optionText = (options || [])
      .map(option => UtilityService.sanitizeHtml(option.optionText.trim()))
      .join(', ');

    const sanitizedName = UtilityService.sanitizeHtml(name);
    const formattedUnitPrice = UtilityService.formatAmount(unitPrice, currency);
    const formattedSetupCost = UtilityService.formatAmount(setupCost, currency);
    const formattedPrice = UtilityService.formatAmount(
      cartSubTotal,
      currency,
      setupCost
    );

    const [quantity, setQuantity] = useState<number | undefined>(
      qty ?? undefined
    );
    const [quantityError, setQuantityError] = useState('');
    const [errorModal, setErrorModal] = useState(false);

    const handleUpdateQuantity = async (updatedQuantity: number) => {
      setQuantityError('');
      setQuantity(updatedQuantity);
      if (updatedQuantity && id) {
        await rootStore
          .updateCartQuantityFlow(id, updatedQuantity)
          .catch(error => {
            // eslint-disable-next-line no-console
            console.error(error);
            cart.setIsLoading(false);
            setErrorModal(true);
          });

        if (cart.quantityError) {
          const maxAvailableQuantity = cart.cartData.items.find(
            item => item.id === id
          )?.qty;
          setQuantityError(`Only ${maxAvailableQuantity} left!`);
          setQuantity(maxAvailableQuantity);
        }
      }
    };

    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);

      if (Number.isNaN(value) || e.target.value === '') {
        setQuantity(undefined);
      } else {
        setQuantity(value);
      }
    };

    const handleOnBlur = async () => {
      if (Number.isNaN(quantity) || quantity === undefined || quantity < 1) {
        handleUpdateQuantity(1);
      }

      if (quantity) {
        handleUpdateQuantity(quantity);
      }
    };

    return (
      <>
        <div data-testid={CartItemTestId} className="m-4 flex flex-col">
          <div className="flex flex-row space-x-4">
            <div>
              <div className="w-28">
                <img
                  className="rounded shadow max-h-28 mx-auto"
                  src={imgUrl}
                  alt={name}
                />
              </div>
            </div>
            <div className="flex flex-col w-full">
              <div className="flex justify-between">
                <div
                  className="text-lg"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: sanitizedName }}
                />
                <button
                  onClick={() =>
                    handleRemoveItem(
                      id as number,
                      options as ItemOptionContract[]
                    )
                  }
                  type="button"
                  className="active:animate-ping"
                >
                  <CloseIcon isSummaryPanel size="12" />
                </button>
              </div>
              <div
                className="text-sm"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: optionText }}
              />
              <div className="flex flex-row justify-end">
                <div className="flex flex-col pr-4">
                  <div className="text-right text-sm">Unit:</div>
                  {setupCost !== 0 && (
                    <div className="text-right text-sm">Setup Cost:</div>
                  )}
                  <div className="text-right">Quantity:</div>
                  <div className="text-right font-semibold">Total:</div>
                </div>
                <div className="flex flex-col">
                  <div
                    className="text-center text-sm"
                    id="FormattedUnitPriceAmount"
                  >
                    {formattedUnitPrice}
                  </div>
                  {setupCost !== 0 && (
                    <div
                      className="text-center text-sm"
                      id="FormattedSetupCostAmount"
                    >
                      {formattedSetupCost}
                    </div>
                  )}
                  <div className="text-right border flex">
                    <button
                      className="qty-counter-btn w-5 border-l active:animate-ping font-medium"
                      id="qty-decrement"
                      type="button"
                      onClick={() =>
                        handleUpdateQuantity(
                          quantity && quantity > 1 ? quantity - 1 : 1
                        )
                      }
                    >
                      -
                    </button>
                    <input
                      type="text"
                      className="qty-counter-input w-8 text-center"
                      id="qty"
                      onChange={handleQuantityChange}
                      onBlur={handleOnBlur}
                      value={quantity ?? ''}
                      maxLength={3}
                    />
                    <button
                      className="qty-counter-btn w-5 border-r active:animate-ping font-medium"
                      id="qty-increment"
                      type="button"
                      onClick={() =>
                        handleUpdateQuantity(quantity ? quantity + 1 : 1)
                      }
                    >
                      +
                    </button>
                  </div>
                  <div
                    className="text-center font-semibold"
                    id="FormattedPriceAmount"
                  >
                    {formattedPrice}
                  </div>
                  <ErrorLabel className="relative top-2.5 text-center">
                    {quantityError}
                  </ErrorLabel>
                </div>
              </div>
            </div>
          </div>
        </div>
        {errorModal && <ErrorModal />}
      </>
    );
  }
);

export default CartItem;
